import React, { useEffect, useRef } from 'react';
import { LiveComment } from '../../types/streaming';
import { StreamNotificationFooter } from '../StreamNotificationFooter';

interface LiveCommentsListProps {
  comments: LiveComment[];
  maxHeight?: string;
}

const CommentTypeIcon: React.FC<{ type: LiveComment['type'] }> = ({ type }) => {
  switch (type) {
    case 'gift':
      return <span className="text-yellow-400">🎁</span>;
    case 'like':
      return <span className="text-red-400">❤️</span>;
    case 'system':
      return <span className="text-blue-400">ℹ️</span>;
    case 'member':
      return <span className="text-green-400">👋</span>;
    default:
      return null;
  }
};

export const LiveCommentsList: React.FC<LiveCommentsListProps> = ({
  comments,
  maxHeight = '400px'
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const isUserScrolling = useRef(false);
  const lastScrollTop = useRef(0);

  // Auto-scroll to bottom when new comments arrive
  useEffect(() => {
    if (listRef.current && !isUserScrolling.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isScrollingUp = element.scrollTop < lastScrollTop.current;
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
    
    lastScrollTop.current = element.scrollTop;
    
    if (isScrollingUp) {
      isUserScrolling.current = true;
    } else if (isAtBottom) {
      isUserScrolling.current = false;
    }
  };

  if (comments.length === 0) {
    return (
      <div 
        className="flex items-center justify-center text-gray-500"
        style={{ height: maxHeight }}
      >
        <div className="text-center">
          <span className="text-4xl mb-2 block">💬</span>
          <p className="text-sm">Comments will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col" style={{ maxHeight }}>
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent"
        style={{ maxHeight: 'calc(100% - 48px)' }}
      >
        {comments.map((comment) => (
          <div
            key={comment.id}
            className={`
              p-3 rounded-lg transition-all
              ${comment.type === 'system' 
                ? 'bg-blue-900/30 border border-blue-800/50' 
                : comment.type === 'gift'
                  ? 'bg-yellow-900/30 border border-yellow-800/50'
                  : 'bg-gray-800/50'
              }
            `}
          >
            <div className="flex items-start gap-2">
              {/* Profile Image or Type Icon */}
              <div className="flex-shrink-0">
                {comment.imageUrl ? (
                  <img
                    src={comment.imageUrl}
                    alt={comment.username}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-sm font-bold text-white">
                    {comment.username.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-white text-sm truncate">
                    {comment.username}
                  </span>
                  <CommentTypeIcon type={comment.type} />
                  {comment.type === 'gift' && comment.giftCount && comment.giftCount > 1 && (
                    <span className="text-xs text-yellow-400 font-medium">
                      x{comment.giftCount}
                    </span>
                  )}
                </div>
                <p className="text-gray-300 text-sm break-words">
                  {comment.message || comment.comment}
                </p>
                {comment.type === 'gift' && comment.giftName && (
                  <div className="mt-1 text-xs text-yellow-400/80">
                    {comment.giftName}
                    {comment.value && ` • ${comment.value} coins`}
                  </div>
                )}
              </div>

              {/* Timestamp */}
              <span className="text-xs text-gray-500 flex-shrink-0">
                {new Date(comment.timestamp).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Stream Notification Footer - shows likes/joins below comments */}
      <div className="flex-shrink-0">
        <StreamNotificationFooter isDarkMode={true} />
      </div>
    </div>
  );
};

export default LiveCommentsList;

